import { router } from '@inertiajs/react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import type { SubmissionReviewListNav } from '@/types';

type Target = NonNullable<SubmissionReviewListNav['prev']>;

// Prev/next arrows that walk the same filtered + sorted review list the admin came
// from, also bound to ← / → (and vim-style k / j). Each move carries the neighbour's
// returnTo, so "Back to Review" and the arrows stay consistent while paging.
export function ReviewListNav({
  nav,
  eventSlug,
}: {
  nav: SubmissionReviewListNav;
  eventSlug: string;
}) {
  const go = useCallback(
    (target: Target | null) => {
      if (!target) return;
      router.visit(
        `/admin/${eventSlug}/submissions/${target.id}?returnTo=${encodeURIComponent(target.returnTo)}`,
      );
    },
    [eventSlug],
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      // Don't hijack the keys while typing (notes, reviews) or inside a dialog/select.
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' ||
          t.isContentEditable)
      )
        return;
      if (
        document.querySelector(
          '[role="dialog"], [role="alertdialog"], [role="listbox"], [role="menu"]',
        )
      )
        return;

      if (e.key === 'ArrowLeft' || e.key === 'k') {
        if (nav.prev) {
          e.preventDefault();
          go(nav.prev);
        }
      } else if (e.key === 'ArrowRight' || e.key === 'j') {
        if (nav.next) {
          e.preventDefault();
          go(nav.next);
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nav, go]);

  return (
    <div className="flex items-center gap-1.5">
      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8"
        disabled={!nav.prev}
        onClick={() => go(nav.prev)}
        title="Previous application (← or k)"
        aria-label="Previous application"
        aria-keyshortcuts="ArrowLeft"
      >
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <span className="min-w-[4rem] text-center text-muted-foreground text-sm tabular-nums">
        {nav.position} / {nav.total}
      </span>
      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8"
        disabled={!nav.next}
        onClick={() => go(nav.next)}
        title="Next application (→ or j)"
        aria-label="Next application"
        aria-keyshortcuts="ArrowRight"
      >
        <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
